"use client";

import { FC, useState } from "react";
import { useRouter } from "next/navigation";
import Typography from "@mui/material/Typography";
import ReplyIcon from "@mui/icons-material/Reply";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Comment as CommentType } from "@/types/social";
import { useAppDispatch } from "@/redux/store";
import { getDisplayedTimeAgo } from "@/utils/date";
import CommentForm from "../CommentForm";
import URLPreview from "../URLPreview";
import UserDetails from "@/components/profile/UserDetails";
import EditIcon from "@/components/icons/Edit";
import DeleteIcon from "@/components/icons/Delete";

interface CommentProps {
  comment: CommentType;
  replies: CommentType[];
  currentUserId: Nullable<number>;
  removeComment: (commentId: number) => void;
  activeComment: any;
  addComment: (text: string, parentId: Nullable<number>) => void;
  updateComment: (text: string, commentId: number) => void;
  setActiveComment: (obj: any) => any;
  parentId: Nullable<number>;
  preview?: { imageUrl: string; url: string };
}

const Comment: FC<CommentProps> = ({
  comment,
  replies,
  currentUserId,
  removeComment,
  activeComment,
  addComment,
  updateComment,
  setActiveComment,
  parentId = null,
  preview,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const canReply = Boolean(currentUserId);
  const canEdit = currentUserId === comment.userId;
  const canDelete = currentUserId === comment.userId;
  const createdAt = getDisplayedTimeAgo(comment.createdAt);
  const isReplying =
    activeComment &&
    activeComment.type === "replying" &&
    activeComment.id === comment.id;
  const isEditing =
    activeComment &&
    activeComment.type === "editing" &&
    activeComment.id === comment.id;
  const replyId = parentId ? parentId : comment.id;
  const [repliesVisible, setRepliesVisible] = useState(false);

  const toggleRepliesVisibility = () => {
    setRepliesVisible(!repliesVisible);
  };

  const handleViewImage = (path: string) => {
    router.push(path);
  };

  return (
    <div className={classes.commentContainer}>
      <div className={classes.userContainer}>
        <UserDetails
          username={comment.username}
          avatarUrl={comment.avatarUrl}
          showAvatar
          subscriptionType={comment.subscriptionType}
          avatarSize="small"
        />
        <div className={classes.createdAt}>
          {createdAt}
          {comment.isEdited ? " (edited) " : " "}
        </div>
      </div>
      {!isEditing && (
        <div>
          {preview && (
            <img
              src={preview.imageUrl}
              alt="preview"
              className={classes.imagePreview}
              onClick={() => handleViewImage(preview.url)}
            />
          )}
          <Typography variant="body1" className={classes.text}>
            {comment.text}
          </Typography>
          <URLPreview commentText={comment.text} />
        </div>
      )}
      {isEditing && (
        <CommentForm
          hasCancelButton
          initialText={comment.text}
          handleSubmit={(text) => updateComment(text, comment.id)}
          handleCancel={() => dispatch(setActiveComment(null))}
          currentUserId={currentUserId}
        />
      )}
      <div className={classes.actionsContainer}>
        {canReply && (
          <IconButton
            className={classes.actionButton}
            onClick={() =>
              dispatch(setActiveComment({ id: comment.id, type: "replying" }))
            }
            aria-label="reply"
          >
            <ReplyIcon style={{ fontSize: "inherit" }} />
          </IconButton>
        )}
        {canEdit && (
          <IconButton
            className={classes.actionButton}
            onClick={() =>
              dispatch(setActiveComment({ id: comment.id, type: "editing" }))
            }
            aria-label="edit"
          >
            <EditIcon style={{ fontSize: "inherit" }} />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            className={classes.actionButton}
            onClick={() => removeComment(comment.id)}
            aria-label="delete"
          >
            <DeleteIcon style={{ fontSize: "inherit" }} />
          </IconButton>
        )}
      </div>
      {isReplying && (
        <CommentForm
          hasCancelButton
          handleSubmit={(text) => addComment(text, replyId)}
          handleCancel={() => dispatch(setActiveComment(null))}
          currentUserId={currentUserId}
        />
      )}
      {replies.length > 0 && (
        <div>
          <button
            className={classes.toggleRepliesButton}
            onClick={toggleRepliesVisibility}
          >
            {repliesVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {replies.length} {replies.length > 1 ? "replies" : "reply"}
          </button>
          {repliesVisible && (
            <div className={classes.replyComments}>
              {replies.map((reply) => (
                <Comment
                  comment={reply}
                  key={reply.id}
                  replies={[]}
                  currentUserId={currentUserId}
                  removeComment={removeComment}
                  activeComment={activeComment}
                  addComment={addComment}
                  updateComment={updateComment}
                  setActiveComment={setActiveComment}
                  parentId={comment.id}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Comment;
